import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { DraftOrder } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { draftOrderKeys } from '../hooks/draft-order-keys';
import { useCancelDraftOrder, useDeleteDraftOrder } from '../hooks/use-draft-order-mutations';
import { draftOrderListSearchSchema } from '../schemas/draft-order-list-search';
import { isStateConflict } from '../schemas/draft-order-rules';

export type DraftConfirm = 'cancel' | 'delete';

export interface ConfirmDraftDialogProps {
  storeId: string;
  draft: DraftOrder;
  action: DraftConfirm | null;
  onOpenChange: (open: boolean) => void;
}

/** Cancelling and deleting both end the draft, so both ask first. Delete goes back to the list. */
export function ConfirmDraftDialog({
  storeId,
  draft,
  action,
  onOpenChange,
}: ConfirmDraftDialogProps) {
  const { t } = useTranslation(['draftOrders', 'common']);
  const message = useErrorMessage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const cancel = useCancelDraftOrder(storeId);
  const remove = useDeleteDraftOrder(storeId);
  const mutation = action === 'delete' ? remove : cancel;
  const error = mutation.error;

  const reset = () => {
    cancel.reset();
    remove.reset();
  };

  const confirm = () => {
    if (action === 'delete') {
      remove.mutate(draft.id, {
        onSuccess: () => {
          onOpenChange(false);
          // The list's search type names every param, so an empty search goes through the schema.
          const search = draftOrderListSearchSchema.parse({});
          void navigate({ to: '/s/$storeId/draft-orders', params: { storeId }, search }).then(() =>
            queryClient.removeQueries({ queryKey: draftOrderKeys.detail(storeId, draft.id) }),
          );
        },
      });
    } else if (action === 'cancel') {
      cancel.mutate(draft.id, { onSuccess: () => onOpenChange(false) });
    }
  };

  return (
    <AlertDialog
      open={action !== null}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t(`draftOrders:confirm.${action ?? 'cancel'}.title`, { name: draft.name })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t(`draftOrders:confirm.${action ?? 'cancel'}.description`)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {isStateConflict(error)
                ? t('draftOrders:confirm.stateChanged', {
                    action: t(
                      action === 'delete'
                        ? 'draftOrders:confirm.deleted'
                        : 'draftOrders:confirm.cancelled',
                    ),
                  })
                : message(error)}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={mutation.isPending}>
            {t('common:actions.back')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={mutation.isPending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              confirm();
            }}
          >
            {t(`draftOrders:confirm.${action ?? 'cancel'}.submit`)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
